import { chipClasses } from "@mui/material/Chip";

const boxShadow =
  "0px 1px 3px 0px rgba(0, 0, 0, 0.30), 0px 4px 8px 3px rgba(0, 0, 0, 0.15)";

export const generalTheme = {
  typography: {
    h5: {
      fontFamily: "Roboto",
      fontSize: "22px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "30px",
    },
    h6: {
      fontFamily: "Roboto",
      fontSize: "16px",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "24px",
    },
    body1: {
      fontFamily: "Roboto",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "20px",
    },
    body2: {
      fontFamily: "Roboto",
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: "16px",
    },
    subtitle1: {
      fontFamily: "Roboto",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "20px",
    },
    subtitle2: {
      fontFamily: "Roboto",
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "16px",
    },
    button: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      textTransform: "none",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "20px",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: ({ palette }) => ({
        body: {
          backgroundColor: palette.background.color,
        },
        ".MuiTabsIndicatorSpan": {
          width: "20px",
          height: "3px",
          borderRadius: "100px 100px 0 0",
          backgroundColor: palette.primary.main,
        },
      }),
    },
    MuiTypography: {
      variants: [
        {
          //TODO move to styled component
          props: { variant: "tableCell" },
          style: ({ theme: { palette } }) => ({
            color: palette.surface.onSurface,
            fontFamily: "Roboto",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 400,
            lineHeight: "20px",
            textAlign: "center",
            textOverflow: "ellipsis",
            overflow: "hidden",
            textWrap: "nowrap",
          }),
        },
      ],
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          display: "flex",
          height: "3px",
          justifyContent: "center",
          backgroundColor: "transparent",
        },
        flexContainer: {
          justifyContent: "flex-end",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme: { palette } }) => ({
          display: "flex",
          gap: "8px",
          padding: "0px 16px",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "48px",
          "&.MuiTab-root>.MuiTab-iconWrapper": {
            margin: 0,
          },
          "&.MuiTab-root:hover": {
            backgroundColor: palette.background.stateLayers.tabHover,
          },
          "&.MuiTab-root:hover.Mui-selected": {
            backgroundColor: palette.background.stateLayers.selectedTabHover,
          },
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        list: ({ theme: { palette } }) => ({
          gap: 0,
          borderRadius: "4px",
          background: palette.surface.surface,
          boxShadow: boxShadow,
          padding: "8px 0px",
        }),
      },
    },
    MuiMenuItem: {
      variants: [
        {
          props: { variant: "NavRail" },
          style: ({ theme: { palette, typography } }) => ({
            ...typography.subtitle2,
            overflow: "hidden",
            color: palette.surface.onSurfaceVariant,
            textAlign: "center",
            textOverflow: "ellipsis",
            "&.MuiMenuItem-root:hover": {
              backgroundColor: "transparent",
            },
            "&.Mui-selected": {
              color: palette.surface.onSurface,
              fontWeight: "600",
              backgroundColor: "transparent",
            },
          }),
        },
        {
          props: { variant: "dropdown" },
          style: {
            display: "flex",
            flex: "1 0 0",
            alignItems: "center",
            alignSelf: "stretch",
            minHeight: "40px !important",
            padding: "4px 24px 4px 16px",
            gap: "16px",
          },
        },
      ],
    },
    MuiList: {
      styleOverrides: {
        root: {
          display: "flex",
          paddingTop: "4px",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "4px",
          flex: "1 0 0",
        },
      },
      variants: [
        {
          props: { variant: "details" },
          style: {
            gap: "1px",
            paddingTop: 0,
            paddingBottom: 0,
            "li:last-child": {
              borderRadius: "0 0 8px 8px",
            },
          },
        },
        {
          props: { variant: "status" },
          style: {
            gap: 0,
            paddingTop: 0,
            paddingBottom: 0,
            "li:last-child": {
              borderRadius: "0 0 8px 8px",
            },
          },
        },
        {
          props: { variant: "notes" },
          style: ({ theme: { palette } }) => ({
            gap: 0,
            paddingTop: 0,
            paddingBottom: 0,
            li: {
              borderRadius: 0,
              borderBottom: `1px solid ${palette.outline.outlineVariant}`,
              padding: "8px 8px 8px 16px",
            },
          }),
        },
      ],
    },
    MuiListItem: {
      styleOverrides: {
        root: ({ theme: { palette } }) => ({
          background: palette.surface.surface,
          gap: "16px",
          borderRadius: "4px",
        }),
      },
      variants: [
        {
          props: { variant: "details" },
          style: {
            padding: "18px 24px 18px 16px",
            borderRadius: 0,
          },
        },
        {
          props: { variant: "file" },
          style: ({ theme: { palette } }) => ({
            borderRadius: "4px",
            border:
              "1px solid " +
              palette.secondary.stateLayers.primaryContainer.primary16,
            background: palette.secondary.secondaryContainer,
            "& .MuiListItemText-root": {
              gap: "8px",
            },
          }),
        },
        {
          props: { variant: "file-error" },
          style: ({ theme: { palette } }) => ({
            borderRadius: "4px",
            minHeight: "56px",
            border:
              "1px solid " +
              palette.secondary.stateLayers.primaryContainer.primary16,
            background: palette.error.stateLayers.onSurface.onSurface8,
            "& .MuiListItemText-root": {
              gap: "8px",
            },
          }),
        },
        {
          props: { variant: "info" },
          style: {
            padding: "18px 24px 18px 16px",
            flexDirection: "column",
            alignItems: "flex-start",
            borderRadius: 0,
          },
        },
      ],
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme: { palette } }) => ({
          display: "flex",
          alignItems: "flex-start",
          flex: "1 0 0",
          padding: "12px 24px 12px 16px",
          marginTop: 0,
          marginBottom: 0,
          gap: "16px",
          alignSelf: "stretch",
          "&.Mui-selected": {
            backgroundColor: palette.secondary.secondaryContainer,
            border:
              "1px solid " +
              palette.primary.stateLayers.onPrimaryContainer.onPrimary12,
            borderRadius: "4px",
          },
          "&.MuiListItemButton-root:hover": {
            backgroundColor: palette.surface.stateLayers.onSurface.onSurface8,
            borderRadius: "4px",
          },
          "&.Mui-selected:hover": {
            backgroundColor:
              palette.secondary.stateLayers.secondaryContainer.onHoverSelected,
          },
        }),
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          flex: "1 0 0",
          marginTop: 0,
          marginBottom: 0,
          wordBreak: "break-all",
          paddingRight: "16px",
        },
        primary: ({ theme: { palette } }) => ({
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",
          alignSelf: "stretch",
          fontFamily: "Roboto",
          fontSize: "14px",
          fontStyle: "normal",
          fontWeight: "500",
          lineHeight: "20px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          color: palette.surface.onSurface,
        }),
        secondary: ({ theme: { palette } }) => ({
          display: "flex",
          alignItems: "flex-start",
          alignSelf: "stretch",
          fontFamily: "Roboto",
          fontSize: "12px",
          fontStyle: "normal",
          fontWeight: "400",
          lineHeight: "16px",
          color: palette.surface.onSurfaceVariant,
        }),
      },
      variants: [
        {
          props: { variant: "success" },
          style: ({ theme: { palette } }) => ({
            "& .MuiListItemText-secondary": {
              color: palette.success.main,
            },
          }),
        },
        {
          props: { variant: "error" },
          style: ({ theme: { palette } }) => ({
            "& .MuiListItemText-secondary": {
              color: palette.error.main,
            },
          }),
        },
        {
          props: { variant: "same-font-size" },
          style: ({ theme: { typography } }) => ({
            "& .MuiListItemText-secondary": {
              ...typography.body1,
            },
          }),
        },
      ],
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          display: "flex",
          padding: "4px 16px",
          justifyContent: "center",
          alignItems: "center",
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        closeAfterTransition: false,
      },
      styleOverrides: {
        paper: {
          borderRadius: "28px",
          form: {
            overflow: "hidden",
            display: "flex",
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          alignSelf: "stretch",
          padding: "0 24px",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          display: "flex",
          padding: "24px 24px 24px 16px",
          justifyContent: "flex-end",
          alignItems: "flex-start",
          gap: "8px",
          alignSelf: "stretch",
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: ({ theme: { palette } }) => ({
          color: palette.surface.onSurfaceVariant,
        }),
      },
    },
    MuiStack: {
      variants: [
        {
          props: { variant: "SwitchField" },
          style: {
            display: "flex",
            flexDirection: "row",
            height: "56px",
            padding: "8px 24px 8px 16px",
            alignItems: "center",
            gap: "16px",
            flex: "1 0 0",
          },
        },
      ],
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme: { palette } }) => ({
          fontFamily: "Roboto",
          fontSize: "14px",
          fontStyle: "normal",
          fontWeight: "500",
          lineHeight: "20px",
          color: palette.surface.onSurface,
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          display: "flex",
          flexDirection: "column",
          alignSelf: "stretch",
          borderRadius: "4px 4px 0px 0px",
          ".MuiInputBase-root": {
            gap: "16px",
          },
          ".MuiInputAdornment-root": {
            marginRight: 0,
          },
          "&.search-field-variant": {
            justifyContent: "center",
            "input:focus::placeholder": {
              color: "transparent",
            },
            "#search-field": {
              padding: 0,
            },
            "& fieldset": {
              border: "none",
            },
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: "contained",
      },
      styleOverrides: {
        root: {
          borderRadius: "100px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "8px 24px",
          gap: "8px",
          minHeight: "36px",
        },
        startIcon: {
          marginRight: 0,
          marginLeft: 0,
        },
        endIcon: {
          margin: 0,
        },
      },
      variants: [
        {
          props: { variant: "subAction" },
          style: ({ theme: { palette } }) => ({
            backgroundColor: palette.surface.surface514,
            "&:hover": {
              backgroundColor: palette.secondary.secondaryContainer,
            },
          }),
        },
      ],
    },
    MuiIconButton: {
      styleOverrides: {
        root: { padding: 0 },
      },
      variants: [
        {
          props: { variant: "filled" },
          style: ({ theme: { palette } }) => ({
            backgroundColor:
              palette.surface.stateLayers.onSurfaceVariant.onSurfaceVariant8,
            padding: "10px",
            width: "40px",
            height: "40px",
            color: palette.surface.onSurfaceVariant,
          }),
        },
        {
          props: { variant: "context-menu" },
          style: ({ theme: { palette } }) => ({
            padding: "9px",
            width: "38px",
            height: "38px",
            color: palette.surface.onSurfaceVariant,
          }),
        },
        {
          props: { variant: "header" },
          style: ({ theme: { palette } }) => ({
            padding: "5px",
            width: "30px",
            height: "30px",
            color: palette.surface.onSurfaceVariant,
          }),
        },
        {
          props: { variant: "input-adornment" },
          style: ({ theme: { palette } }) => ({
            padding: 0,
            width: "20px",
            height: "20px",
            color: palette.surface.onSurfaceVariant,
          }),
        },
      ],
    },
    MuiAvatar: {
      styleOverrides: {
        root: ({ theme: { palette } }) => ({
          display: "flex",
          width: "32px",
          height: "32px",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: palette.warning.main,
        }),
      },
    },
    MuiFab: {
      defaultProps: {
        color: "secondary",
      },
      styleOverrides: {
        root: {
          borderRadius: "16px",
          boxShadow: boxShadow,
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {},
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: ({ theme: { palette } }) => ({
          borderRadius: "0 0 8px 8px",
          backgroundColor: palette.primary.onPrimary,
          "&::-webkit-scrollbar": {
            width: "8px",
            borderLeft: "1px solid var(--background-background, #F0F0F3)",
          },
        }),
      },
      variants: [
        {
          props: { variant: "error" },
          style: {
            display: "flex",
            alignSelf: "stretch",
            flexDirection: "column",
            flex: "1 0 0",
          },
        },
        {
          props: { variant: "loading" },
          style: {
            display: "flex",
            alignSelf: "stretch",
            flexDirection: "column",
            flex: "1 0 0",
            padding: "16px",
            gap: "10px",
          },
        },
      ],
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme: { palette } }) => ({
          height: 20,
          alignItems: "center",
          alignSelf: "stretch",
          "&:nth-of-type(even)": {
            backgroundColor: palette.surface.surface,
          },
          "&.MuiTableRow-hover:hover": {
            backgroundColor: palette.surface.stateLayers.onSurface.onSurface8,
          },
          "&.Mui-selected": {
            backgroundColor: palette.secondary.secondaryContainer,
          },
          "&.Mui-selected:hover": {
            backgroundColor:
              palette.secondary.stateLayers.secondaryContainer.onHoverSelected,
          },
        }),
      },
      variants: [
        {
          props: { variant: "header" },
          style: ({ theme: { palette } }) => ({
            background: palette.surface.surface,
          }),
        },
      ],
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme: { palette } }) => ({
          height: 20,
          alignItems: "center",
          alignSelf: "stretch",
          padding: "12px",
          borderBottom: "1px solid " + palette.background.color,
        }),
      },
    },
    MuiCheckbox: {
      variants: [
        {
          props: { variant: "table" },
          style: {
            padding: 0,
            width: "20px",
            height: "20px",
          },
        },
      ],
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: {
          margin: "0 4px",
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(224, 226, 236, 0.08)",
        },
        rounded: {
          borderRadius: "100px",
        },
        rectangular: {
          borderRadius: "8px",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme: { palette } }) => ({
          backgroundColor: palette.surface.inverseSurface,
        }),
        arrow: ({ theme: { palette } }) => ({
          color: palette.surface.inverseSurface,
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme: { palette } }) => ({
          borderRadius: "8px",
          gap: "8px",
          justifyContent: "start",
          padding: "6px 8px 6px 8px",
          height: "auto",
          width: "fit-content",
          background: palette.surface.surface28,
          [`& .${chipClasses.icon}`]: {
            margin: 0,
            color: "inherit",
          },
        }),
        label: ({ theme: { typography } }) => ({
          ...typography.subtitle1,
          padding: 0,
        }),
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          width: "100%",
        },
        determinate: ({ theme: { palette } }) => ({
          backgroundColor: palette.outline.outlineVariant,
        }),
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        labelContainer: ({ theme: { palette, typography } }) => ({
          color: palette.surface.onSurface,
          ...typography.subtitle1,
        }),
        label: ({ theme: { palette, typography } }) => ({
          color: palette.surface.onSurfaceVariant,
          ...typography.subtitle2,
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ theme: { typography } }) => ({
          borderRadius: "4px",
          borderWidth: "1px",
          borderStyle: "solid",
          ...typography.subtitle1,
        }),
        filledWarning: ({ theme: { palette } }) => ({
          backgroundColor: palette.warning.warningContainer,
          color: palette.warning.onWarningContainer,
          borderColor: palette.warning.stateLayers.warning.warning12,
        }),
        filledError: ({ theme: { palette } }) => ({
          backgroundColor: palette.error.errorContainer,
          color: palette.error.onErrorContainer,
          borderColor: palette.error.stateLayers.error.error12,
        }),
        filledInfo: ({ theme: { palette } }) => ({
          backgroundColor: palette.primary.primaryContainer,
          color: palette.primary.onPrimaryContainer,
          borderColor: palette.primary.stateLayers.primary.primary12,
        }),
        filledSuccess: ({ theme: { palette } }) => ({
          backgroundColor: palette.tertiary.tertiaryContainer,
          color: palette.tertiary.onTertiaryContainer,
          borderColor: palette.tertiary.stateLayers.tertiary.tertiary12,
        }),
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ theme: { palette } }) => ({
          color: palette.outline.outlineVariant,
        }),
      },
    },
  },
};
